import {
    ExportOutlined,
    FileOutlined,
    FolderOpenOutlined,
    LoadingOutlined,
    MenuOutlined,
    SaveOutlined,
    VideoCameraOutlined
} from '@ant-design/icons';
import {Button, Col, Drawer, Row, Tabs} from 'antd';
import Canvg, {presets} from 'canvg';
import _ from 'lodash';
import React, {useState} from 'react';
import {connect} from 'react-redux';
import {AnyAction} from "redux";
import {ThunkDispatch} from "redux-thunk";
import EditableLabel from '../../components/EditableLabel';
import {findQuery} from '../../reducers/util';
import {AppState} from '../../types/AppState';
import {Query} from '../HealthcareUsageReport/types/CohortQuery';
import ListQueries from '../../components/cohort-queries/ListQueries';
import {newPathway, savePathway, setCurrentPathwayTitle} from './actions';
import DisplayEvents from './DisplayEvents';
import Pathway from './Pathway';
import PathwayFilters from './PathwayFilters';
import {findPathway} from './reducers';
import SavedPathwaysList from './SavedPathwaysList';
import {CohortPathway, EventConfig} from './types/CohortPathway';

const { TabPane } = Tabs;

const TAB_COHORTS_KEY = "1"
const TAB_PATHWAYS_KEY = "2"

// copied from import { DrawerProps } from "antd/lib/drawer";
declare const PlacementTypes: ["top", "right", "bottom", "left"];
declare type placementType = typeof PlacementTypes[number];

interface Props {
    placement: placementType;
    open: boolean;
    onClose(): any;
    content: JSX.Element
}

const CustomDrawer = ({ placement, open, onClose, content }: Props) => {

    return (
        <Drawer
            placement={placement}
            closable={false}
            onClose={onClose}
            open={open}
            headerStyle={{ display: 'none' }}
            width="500"
        >
            {content}
        </Drawer>
    )

}

interface PathwayViewerProps {
    query: Query,
    pathway: CohortPathway,
    currentFilter: EventConfig | undefined,
    setPathwayTitle(title: string): void
    newPathway(queryId: number): void
    savePathway(pathwayId: number, queryId: number): void
}

const PathwayViewer = ({ query, pathway, currentFilter, setPathwayTitle, newPathway, savePathway }: PathwayViewerProps) => {
    const leftDrawerVisible = currentFilter != null
    const [rightDrawerVisible, setRightDrawerVisible] = useState(false);
    const [activeKey, setActiveKey] = useState(TAB_COHORTS_KEY);

    const onLeftClose = () => {
        console.log('onLeftClose')
    }

    const showRightDrawer = (activeKey: string) => {
        setActiveKey(activeKey)
        setRightDrawerVisible(true);
    }

    const onRightClose = () => {
        setRightDrawerVisible(false);
    }

    const Filters = () => {
        if (_.isNil(currentFilter)) {
            return (
                <span />
            )
        } else {
            return (
                <PathwayFilters />
            )
        }
    }

    const Cohorts = () => {
        return (
            <div>
                <Tabs type="card" activeKey={activeKey} onChange={(activeKey) => setActiveKey(activeKey)}>
                    <TabPane tab="My Cohorts" key={TAB_COHORTS_KEY}>
                        <ListQueries />
                    </TabPane>
                    <TabPane tab="Saved Pathways" key={TAB_PATHWAYS_KEY}>
                        <SavedPathwaysList />
                    </TabPane>
                </Tabs>
            </div>
        )
    }

    // https://stackoverflow.com/questions/16049538/how-to-convert-save-d3-js-graph-to-pdf-jpeg
    const exportSvgAsPng = async (name: string) => {
        const svg = document
            .getElementById("pathway-viewer-diagram")!
            .querySelector("svg")!.outerHTML
        const canvas = new OffscreenCanvas(1000, 1000)
        const ctx = canvas.getContext('2d')
        // @ts-ignore
        const canvg = await Canvg.from(ctx!, svg, presets.offscreen())

        // Render only first frame, ignoring animations and mouse.
        await canvg.render()

        const blob = await canvas.convertToBlob()
        const pngUrl = URL.createObjectURL(blob)

        var downloadLink = document.createElement("a")
        downloadLink.href = pngUrl
        downloadLink.download = name + ".png"
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
    }

    const content = () => {
        if (_.isNil(pathway) && query.pathwayStatus === 'COMPLETE') {
            return <LoadingOutlined />
        } else if (_.isNil(pathway)) {
            return <span>The cohort pathway status is {query.pathwayStatus}, please generate the pathways first before trying to explore pathways.</span>
        } else {
            return [<DisplayEvents key="0" />, <Pathway key="1" />]
        }
    }

    const disableButtons = _.isNil(pathway)

    return <div className="pathway-viewer">
        <Row>
            <Col className="pathway-header" span={24}>
                <div className="title-container">
                    {disableButtons ? null : <EditableLabel text={pathway.title} onSaveNotification={(value: string) => setPathwayTitle(value)} />}
                </div>
                <div className="button-container">
                    <CustomDrawer placement={"left"} open={leftDrawerVisible} onClose={onLeftClose} content={Filters()} />
                    <Button className="video-tour-btn" icon={<VideoCameraOutlined />}>Video Tour Guide</Button>
                    <Button className="new-btn" icon={<FileOutlined />} onClick={() => newPathway(query.id)} disabled={disableButtons}>New</Button>
                    <Button className="save-btn" icon={<SaveOutlined />} onClick={() => savePathway(pathway.id, query.id)} disabled={disableButtons}>Save</Button>
                    <Button className="save-pathways-btn" icon={<FolderOpenOutlined />} onClick={() => showRightDrawer(TAB_PATHWAYS_KEY)}>Saved Pathways</Button>
                    <Button className="my-cohorts-btn" icon={<MenuOutlined />} type="primary" onClick={() => showRightDrawer(TAB_COHORTS_KEY)}>My Cohorts</Button>
                    <CustomDrawer placement={"right"} open={rightDrawerVisible} onClose={onRightClose} content={Cohorts()} />
                    <Button className="export-btn" type="primary" icon={<ExportOutlined />} onClick={() => exportSvgAsPng(pathway.title)} disabled={disableButtons}>Export</Button>
                </div>
            </Col>
            <Col className="pathway-content" span={24}>
                {content()}
            </Col>
        </Row>
    </div>
}

const mapStateToProps = (state: AppState, ownProps: any) => {
    const query = findQuery(state.current.study, state.current.queryId!)
    const pathway = findPathway(state.pathway.pathways, state.pathway.pathwayId!)
    return {
        query: query,
        pathway: pathway,
        currentFilter: state.pathway.currentFilter,
        ...ownProps
    }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    return {
        setPathwayTitle: (title: string) => {
            dispatch(setCurrentPathwayTitle(title))
        },
        newPathway: (queryId: number) => {
            dispatch(newPathway(queryId))
        },
        savePathway: (pathwayId: number, queryId: number) => {
            dispatch(savePathway(pathwayId, queryId))
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PathwayViewer)